import { createAuth0Client } from "@auth0/auth0-spa-js";
import SessionStorage from "helpers/SessionStorage";
import React, { useContext, useEffect, useState } from "react";
import { purgeLegacyStorageKeys, purgeUserScopedStorage } from "src/helpers/scopedStorage";
import { useAppStore } from "src/state/appStore";

const { VITE_AUTH0_CLIENTID, VITE_PUBLIC_URL } = window.__ENV;

export const Auth0Context = React.createContext();
export const useAuth0 = () => useContext(Auth0Context);

const Auth0Provider = ({ children, ...initOptions }) => {
	const [user, setUser] = useState();
	const [auth0Client, setAuth0] = useState();
	const [popupOpen, setPopupOpen] = useState(false);
	const setAuthIsAuthenticated = useAppStore(s => s.setAuthIsAuthenticated);
	const setIsLoading = useAppStore(s => s.setAuthIsLoading);
	const tenantName = useAppStore(s => s.tenantName);
	const setTenantName = useAppStore(s => s.setTenantName);

	const ITEM_PATHNAME = "pathname";

	const onRedirectCallback = ({ appState }) => {
		const pathname = appState?.targetUrl ? appState.targetUrl : SessionStorage.getLocation();
		const tenant = pathname.split("/")[1];
		if (pathname === "/logout") {
			localStorage.setItem("tenantName", tenant);
		}
		setTenantName(tenant);
		const jumpLink = `${window.location.origin}${SessionStorage.getLocation()}`;
		window.location.href = jumpLink;
	};

	useEffect(() => {
		// Set tenantName from current URL
		const pathname = window.location.pathname;

		if (!pathname.includes("/logout")) {
			const tenant = pathname.split("/")[1];
			if (tenant) {
				setTenantName(tenant);
				localStorage.setItem("tenantName", tenant);
			}
		}
		const initAuth0 = async () => {
			const auth0FromHook = await createAuth0Client(initOptions);
			setAuth0(auth0FromHook);

			if (window.location.search.includes("code=")) {
				const appState = await auth0FromHook.handleRedirectCallback();
				onRedirectCallback(appState);
			}

			const _isAuthenticated = await auth0FromHook.isAuthenticated();
			setAuthIsAuthenticated(_isAuthenticated);

			if (_isAuthenticated) {
				const user = await auth0FromHook.getUser();
				setUser(user);
			}
			setIsLoading(false);
		};

		initAuth0();

		return () => {
			const jumpLink = `${window.location.origin}${SessionStorage.getLocation()}`;
			window.location.href = jumpLink;
		};
	}, []);

	const loginWithPopup = async (params = {}) => {
		SessionStorage.setLocation(window.location);
		setPopupOpen(true);
		try {
			await auth0Client.loginWithPopup(params);
		} catch (error) {
			Log.error(error);
		} finally {
			setPopupOpen(false);
		}
		const user = await auth0Client.getUser();
		setUser(user);
		setAuthIsAuthenticated(true);
	};

	const handleRedirectCallback = async () => {
		setIsLoading(true);
		await auth0Client.handleRedirectCallback();
		const user = await auth0Client.getUser();
		setIsLoading(false);
		setAuthIsAuthenticated(true);
		setUser(user);
	};

	const signOut = async (params = {}) => {
		purgeUserScopedStorage(tenantName, useAppStore.getState().currentUser?.id);
		purgeLegacyStorageKeys();
		// Make sure we have the current tenant in localStorage
		if (tenantName) {
			localStorage.setItem("tenantName", tenantName);
		}

		const returnToUrl = `${window.location.origin}/${VITE_PUBLIC_URL}/logout`;

		try {
			await auth0Client.logout({
				...params,
				clientId: VITE_AUTH0_CLIENTID,
				logoutParams: {
					returnTo: returnToUrl
				}
			});

			setAuthIsAuthenticated(false);
			setUser(null);
		} catch (error) {
			console.error("Logout error: ", error);
		}
	};

	function extractTenant(pathname, envPrefix) {
		const parts = pathname.split("/").filter(Boolean);
		if (parts[0] === envPrefix) parts.shift();

		const candidate = parts[0];
		if (!candidate || candidate === "logout") {
			return localStorage.getItem("tenantName") ?? "";
		}
		return candidate;
	}

	return (
		<Auth0Context.Provider
			value={{
				user,
				popupOpen,
				loginWithPopup,
				handleRedirectCallback,
				getIdTokenClaims: (...p) => auth0Client.getIdTokenClaims(...p),
				loginWithRedirect: async (...p) => {
					SessionStorage.setLocation(window.location);
					if (auth0Client && auth0Client.loginWithRedirect) {
						try {
							auth0Client.loginWithRedirect(...p);
						} catch (e) {
							if (["login_required", "missing_refresh_token", "invalid_grant"].includes(e.error)) {
								this.signOut(...p);
							}
							throw e;
						}

						const user = await auth0Client.getUser();
						setUser(user);
						setAuthIsAuthenticated(user.isAuthenticated);
					}
				},
				getTokenSilently: (...p) => auth0Client.getTokenSilently(...p),
				getTokenWithPopup: (...p) => auth0Client.getTokenWithPopup(...p),
				logout: (...p) => signOut(...p)
			}}>
			{children}
		</Auth0Context.Provider>
	);
};

export default Auth0Provider;